import { useConfirmation } from '../contexts/ConfirmationContext';
import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import toast from 'react-hot-toast';
import PageHeader from '../components/ui/PageHeader';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../services/authService';
import { PlatformService } from '../services/platformService';
import { ScheduleService } from '../services/scheduleService';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ChangePasswordModal } from '../components/ui/ChangePasswordModal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Platform, Schedule } from '../types';
import {
  UserIcon,
  KeyIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  PencilIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export const UserSettingsPage: React.FC = () => {
  const { user, setUser } = useAuth();
  const confirm = useConfirmation();
  const [passwordState, setPasswordState] = useState({ dirty: false, busy: false });
  const handlePasswordDirty = useCallback((dirty: boolean, busy: boolean) => setPasswordState(previous => previous.dirty === dirty && previous.busy === busy ? previous : { dirty, busy }), []);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isUpdatingIntensity, setIsUpdatingIntensity] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [lektoratHinweise, setLektoratHinweise] = useState<string[]>([]);
  const [isLoadingHinweise, setIsLoadingHinweise] = useState(false);
  const [hinweiseError, setHinweiseError] = useState(false);
  const [reloadHinweise, setReloadHinweise] = useState(0);
  const [isUpdatingHinweise, setIsUpdatingHinweise] = useState(false);
  const [newHinweis, setNewHinweis] = useState('');
  const [editingHinweisIndex, setEditingHinweisIndex] = useState<number | null>(null);
  const [editingHinweisText, setEditingHinweisText] = useState('');
  const hintDirty = !!newHinweis || (editingHinweisIndex !== null && editingHinweisText !== lektoratHinweise[editingHinweisIndex]);


  const userId = user?.id;

  // Plattformen und Termine beim Komponenten-Mount laden
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;

      // Plattformen laden
      setIsLoadingPlatforms(true);
      try {
        const allPlatforms = await PlatformService.getAllPlatforms();
        setPlatforms(allPlatforms);
      } catch (error) {
        toast.error('Die zugewiesenen Plattformen konnten nicht geladen werden.');
      } finally {
        setIsLoadingPlatforms(false);
      }

      // Termine des Benutzers laden
      setIsLoadingSchedules(true);
      try {
        const userSchedules = await ScheduleService.getSchedulesByAuthor(userId);
        setSchedules(userSchedules);
      } catch (error) {
        toast.error('Die zugewiesenen Termine konnten nicht geladen werden.');
      } finally {
        setIsLoadingSchedules(false);
      }

      // Lektorat-Hinweise laden
      setIsLoadingHinweise(true);
      try {
        const hinweise = await AuthService.getLektoratHinweise(userId);
        setLektoratHinweise(hinweise); setHinweiseError(false);
      } catch (error) {
        setHinweiseError(true);
        console.error('Fehler beim Laden der Lektorat-Hinweise:', error);
        toast.error('Die Lektorat-Hinweise konnten nicht geladen werden.');
      } finally {
        setIsLoadingHinweise(false);
      }
    };

    loadData();
  }, [userId, reloadHinweise]);

  // Plattform-Name anhand der ID finden
  const getPlatformName = (platformId: string): string => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.name || `Unbekannte Plattform (${platformId})`;
  };

  // Termin-Formatierung
  const formatSchedule = (schedule: Schedule): string => {
    const dayName = ScheduleService.getDayName(schedule.dayOfWeek);
    const platformName = getPlatformName(schedule.platformId);
    return `${dayName} ${schedule.time} - ${platformName}`;
  };

  const handlePasswordChangeSuccess = () => {
    // Hier könnten weitere Aktionen nach erfolgreicher Passwort-Änderung hinzugefügt werden
  };

  const handleIntensityChange = async (intensity: 'rechtschreibung' | 'leicht' | 'tiefgreifend') => {
    if (!user) return;

    setIsUpdatingIntensity(true);
    try {
      const updatedUser = await AuthService.updateUserPreferences(user.id, {
        preferredLektoratIntensity: intensity
      });
      setUser(updatedUser);
      toast.success('Lektorat-Einstellung gespeichert.');
    } catch (error) {
      toast.error('Die Lektorat-Einstellung konnte nicht gespeichert werden. Bitte versuche es erneut.');
    } finally {
      setIsUpdatingIntensity(false);
    }
  };

  const handleAddHinweis = async () => {
    if (!user || !newHinweis.trim() || isUpdatingHinweise || isLoadingHinweise || hinweiseError) return;

    setIsUpdatingHinweise(true);
    try {
      const updatedHinweise = [...lektoratHinweise, newHinweis.trim()];
      await AuthService.saveLektoratHinweise(user.id, updatedHinweise);
      setLektoratHinweise(updatedHinweise);
      setNewHinweis('');
      toast.success('Hinweis gespeichert.');
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Hinweises:', error);
      toast.error('Der Hinweis konnte nicht gespeichert werden. Deine Eingabe bleibt erhalten.');
    } finally {
      setIsUpdatingHinweise(false);
    }
  };

  const handleEditHinweis = async (index: number) => {
    if (!user || !editingHinweisText.trim() || isUpdatingHinweise || hinweiseError) return;

    setIsUpdatingHinweise(true);
    try {
      const updatedHinweise = [...lektoratHinweise];
      updatedHinweise[index] = editingHinweisText.trim();
      await AuthService.saveLektoratHinweise(user.id, updatedHinweise);
      setLektoratHinweise(updatedHinweise);
      setEditingHinweisIndex(null);
      setEditingHinweisText('');
      toast.success('Hinweis gespeichert.');
    } catch (error) {
      console.error('Fehler beim Bearbeiten des Hinweises:', error);
      toast.error('Der Hinweis konnte nicht gespeichert werden. Deine Eingabe bleibt erhalten.');
    } finally {
      setIsUpdatingHinweise(false);
    }
  };

  const handleDeleteHinweis = async (index: number) => {
    if (!user || isUpdatingHinweise || hinweiseError || !(await confirm({ title: 'Hinweis löschen?', description: 'Dieser Hinweis wird künftig beim Lektorat nicht mehr berücksichtigt.', confirmLabel: 'Hinweis löschen', danger: true }))) return;

    setIsUpdatingHinweise(true);
    try {
      const updatedHinweise = lektoratHinweise.filter((_, i) => i !== index);
      await AuthService.saveLektoratHinweise(user.id, updatedHinweise);
      setLektoratHinweise(updatedHinweise);
      toast.success('Hinweis gelöscht.');
    } catch (error) {
      console.error('Fehler beim Löschen des Hinweises:', error);
      toast.error('Der Hinweis konnte nicht gespeichert werden. Deine Eingabe bleibt erhalten.');
    } finally {
      setIsUpdatingHinweise(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <PageHeader title={<>Benutzereinstellungen</>} description={<>Verwalte deine Kontoeinstellungen und Sicherheit</>} />
      </div>

      <UnsavedChangesNotice dirty={hintDirty || passwordState.dirty} busy={isUpdatingIntensity || isUpdatingHinweise || passwordState.busy} />

      {/* Lektorat-Einstellungen */}
      <div className="card">
        <div className="flex items-center mb-4">
          <SparklesIcon className="w-6 h-6 text-gray-400 mr-3" />
          <h2 className="text-lg font-medium text-gray-900">Lektorat-Einstellungen</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bevorzugte Lektorat-Intensität
            </label>
            <p className="text-sm text-gray-500 mb-4">
              Diese Einstellung wird als Standard für neue Artikel verwendet.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button type="button" disabled={isUpdatingIntensity} aria-pressed={user?.preferredLektoratIntensity === 'rechtschreibung'} className={`p-4 text-left border rounded-lg cursor-pointer transition-colors ${
                  user?.preferredLektoratIntensity === 'rechtschreibung'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isUpdatingIntensity ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isUpdatingIntensity && handleIntensityChange('rechtschreibung')}
              >
                <div className="flex items-center mb-2">
                  <PencilIcon className="w-5 h-5 text-gray-600 mr-2" />
                  <h3 className="font-medium text-gray-900">Rechtschreibung</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Nur Orthografie, Grammatik und Zeichensetzung
                </p>
              </button>

              <button type="button" disabled={isUpdatingIntensity} aria-pressed={user?.preferredLektoratIntensity === 'leicht'} className={`p-4 text-left border rounded-lg cursor-pointer transition-colors ${
                  user?.preferredLektoratIntensity === 'leicht'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isUpdatingIntensity ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isUpdatingIntensity && handleIntensityChange('leicht')}
              >
                <div className="flex items-center mb-2">
                  <SparklesIcon className="w-5 h-5 text-gray-600 mr-2" />
                  <h3 className="font-medium text-gray-900">Leicht</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Stilglättung, Kürzen, keine Bedeutungsänderung
                </p>
              </button>

              <button type="button" disabled={isUpdatingIntensity} aria-pressed={user?.preferredLektoratIntensity === 'tiefgreifend'} className={`p-4 text-left border rounded-lg cursor-pointer transition-colors ${
                  user?.preferredLektoratIntensity === 'tiefgreifend'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isUpdatingIntensity ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isUpdatingIntensity && handleIntensityChange('tiefgreifend')}
              >
                <div className="flex items-center mb-2">
                  <WrenchScrewdriverIcon className="w-5 h-5 text-gray-600 mr-2" />
                  <h3 className="font-medium text-gray-900">Tiefgreifend</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Deutlich umformulieren, Inhalt unverändert
                </p>
              </button>
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-blue-400 mr-2 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Aktuelle Einstellung: {user?.preferredLektoratIntensity || 'leicht'}</p>
                <p className="mt-1">Diese Einstellung wird automatisch beim Erstellen neuer Artikel verwendet. Du kannst sie auch pro Artikel individuell anpassen.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lektorat-Hinweise */}
      <div className="card">
        <div className="flex items-center mb-4">
          <SparklesIcon className="w-6 h-6 text-gray-400 mr-3" />
          <h2 className="text-lg font-medium text-gray-900">Lektorat-Hinweise</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Hier kannst du Hinweise für das KI-Lektorat verwalten. Diese werden automatisch bei jedem neuen Lektorat berücksichtigt.
        </p>

        {hinweiseError && <p role="alert" className="mb-4 text-sm text-red-700">Deine Hinweise konnten nicht geladen werden. <Button variant="outline" size="sm" onClick={() => setReloadHinweise(n => n + 1)}>Erneut versuchen</Button></p>}
        {/* Neuen Hinweis hinzufügen */}
        <div className="mb-6">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end">
            <Input
              value={newHinweis}
              onChange={(e) => setNewHinweis(e.target.value)}
              label="Neuer Lektorat-Hinweis"
              disabled={isUpdatingHinweise || isLoadingHinweise || hinweiseError}
              placeholder="Neuen Hinweis eingeben..."
              className="flex-1"
            />
            <Button
              onClick={handleAddHinweis}
              disabled={!newHinweis.trim() || isUpdatingHinweise || isLoadingHinweise || hinweiseError}
              size="sm"
            >
              {isUpdatingHinweise ? <LoadingSpinner size="sm" /> : 'Hinzufügen'}
            </Button>
          </div>
        </div>

        {/* Bestehende Hinweise */}
        {isLoadingHinweise ? (
          <LoadingSpinner />
        ) : hinweiseError ? null : lektoratHinweise.length > 0 ? (
          <div className="space-y-3">
            {lektoratHinweise.map((hinweis, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                {editingHinweisIndex === index ? (
                  <div className="flex flex-wrap items-end gap-2 flex-1">
                    <Input
                      label="Hinweis bearbeiten"
                      disabled={isUpdatingHinweise}
                      value={editingHinweisText}
                      onChange={(e) => setEditingHinweisText(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => handleEditHinweis(index)}
                      disabled={!editingHinweisText.trim() || isUpdatingHinweise}
                      size="sm"
                    >
                      {isUpdatingHinweise ? <LoadingSpinner size="sm" /> : 'Speichern'}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={isUpdatingHinweise}
                      onClick={async () => {
                        if (hintDirty && !(await confirm({ title: 'Hinweisänderungen verwerfen?', description: 'Die aktuelle Eingabe ist noch nicht gespeichert.', confirmLabel: 'Änderungen verwerfen', danger: true }))) return;
                        setEditingHinweisIndex(null);
                        setEditingHinweisText('');
                      }}
                      size="sm"
                    >
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm text-gray-700 flex-1">{hinweis}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        aria-label="Hinweis bearbeiten"
                        disabled={isUpdatingHinweise}
                        onClick={() => {
                          void (async () => {
                            if (hintDirty && !(await confirm({ title: 'Hinweisänderungen verwerfen?', description: 'Die aktuelle Eingabe ist noch nicht gespeichert.', confirmLabel: 'Änderungen verwerfen', danger: true }))) return;
                            setEditingHinweisIndex(index); setEditingHinweisText(hinweis);
                          })();
                        }}
                        size="sm"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteHinweis(index)}
                        disabled={isUpdatingHinweise}
                        size="sm"
                      >
                        {isUpdatingHinweise ? <LoadingSpinner size="sm" /> : 'Löschen'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p>Du hast noch keine Lektorat-Hinweise gespeichert.</p>
                <p className="mt-1">Hinweise werden automatisch bei jedem Lektorat berücksichtigt.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Benutzerinformationen */}
      <div className="card">
        <div className="flex items-center mb-4">
          <UserIcon className="w-6 h-6 text-gray-400 mr-3" />
          <h2 className="text-lg font-medium text-gray-900">Kontoinformationen</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Name"
            value={user?.name || ''}
            disabled
            helperText="Name kann über die Administration geändert werden"
          />
          <Input
            label="E-Mail"
            type="email"
            value={user?.email || ''}
            disabled
            helperText="E-Mail-Adresse kann nicht geändert werden"
          />
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex">
            <InformationCircleIcon className="w-5 h-5 text-blue-400 mr-2 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Rollen: {user?.roles.join(', ')}</p>
              <p className="mt-1">Deine Benutzerrolle bestimmt deine Berechtigungen im System.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sicherheit */}
      <div className="card">
        <div className="flex items-center mb-4">
          <ShieldCheckIcon className="w-6 h-6 text-gray-400 mr-3" />
          <h2 className="text-lg font-medium text-gray-900">Sicherheit</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <KeyIcon className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-gray-900">Passwort</h3>
                <p className="text-sm text-gray-500">Ändere dein Passwort regelmäßig für mehr Sicherheit</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsChangePasswordModalOpen(true)}
            >
              Passwort ändern
            </Button>
          </div>

          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-yellow-400 mr-2 mt-0.5" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium">Sicherheitshinweise:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Verwende ein starkes Passwort mit mindestens 8 Zeichen</li>
                  <li>Kombiniere Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen</li>
                  <li>Ändere dein Passwort regelmäßig</li>
                  <li>Teile dein Passwort niemals mit anderen</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zugewiesene Plattformen */}
      {user?.assignedPlatforms && user.assignedPlatforms.length > 0 && (
        <div className="card">
          <div className="flex items-center mb-4">
            <UserIcon className="w-6 h-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">Zugewiesene Plattformen</h2>
          </div>

          {isLoadingPlatforms ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-2">
              {user.assignedPlatforms.map((platformId, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">
                    {getPlatformName(platformId)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p>Du hast Zugriff auf {user.assignedPlatforms.length} Plattform(en).</p>
                <p className="mt-1">Plattform-Zuweisungen können über die Administration geändert werden.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zugewiesene Termine */}
      {isLoadingSchedules ? (
        <LoadingSpinner />
      ) : schedules.length > 0 ? (
        <div className="card">
          <div className="flex items-center mb-4">
            <CalendarIcon className="w-6 h-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">Zugewiesene Termine</h2>
          </div>
          <div className="space-y-2">
            {schedules.map((schedule, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div className="flex items-center text-sm text-gray-700">
                  <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span>{formatSchedule(schedule)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p>Du hast {schedules.length} Termin(e) zugewiesen.</p>
                <p className="mt-1">Termine können über die Administration geändert werden.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center mb-4">
            <CalendarIcon className="w-6 h-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">Zugewiesene Termine</h2>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p>Du hast noch keine Termine zugewiesen.</p>
                <p className="mt-1">Termine können über die Administration geändert werden.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Passwort-Änderungs-Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
        onDirtyChange={handlePasswordDirty}
      />
    </div>
  );
};
